/**
 * Graceful Degradation Manager
 * 
 * Phase 4: Error Handling
 * 
 * Features:
 * - Fallback mechanisms for failed operations
 * - Service health monitoring
 * - Automatic feature degradation
 * - Recovery strategies
 * 
 * @author BitPanel Team
 * @date 2025-10-28
 */

/**
 * Service health status
 */
export const HealthStatus = {
  HEALTHY: 'HEALTHY',       // All systems operational
  DEGRADED: 'DEGRADED',     // Some features unavailable
  UNHEALTHY: 'UNHEALTHY',   // Critical features unavailable
  DOWN: 'DOWN'              // Service completely down
};

/**
 * Feature flags for degradation
 */
export const Feature = {
  REAL_TIME_NOTIFICATIONS: 'REAL_TIME_NOTIFICATIONS',
  NOTIFICATION_HISTORY: 'NOTIFICATION_HISTORY',
  NOTIFICATION_BADGES: 'NOTIFICATION_BADGES',
  TOAST_NOTIFICATIONS: 'TOAST_NOTIFICATIONS',
  SOUND_NOTIFICATIONS: 'SOUND_NOTIFICATIONS'
};

/**
 * Graceful Degradation Manager Class
 * 
 * Manages service degradation and recovery
 */
export class GracefulDegradationManager {
  constructor(options = {}) {
    this.healthCheckInterval = options.healthCheckInterval || 30000; // 30 seconds
    this.degradationThreshold = options.degradationThreshold || 3; // 3 failures
    this.recoveryThreshold = options.recoveryThreshold || 2; // 2 successes
    this.enableAutoRecovery = options.enableAutoRecovery !== false;
    
    // Service health
    this.healthStatus = HealthStatus.HEALTHY;
    this.featureStatus = new Map();
    
    // Initialize all features as enabled
    Object.values(Feature).forEach(feature => {
      this.featureStatus.set(feature, {
        enabled: true,
        failureCount: 0,
        successCount: 0,
        lastFailure: null,
        lastSuccess: null
      });
    });
    
    // Failure tracking
    this.consecutiveFailures = 0;
    this.consecutiveSuccesses = 0;
    
    // Statistics
    this.stats = {
      totalDegradations: 0,
      totalRecoveries: 0,
      degradationsByFeature: {},
      currentlyDegraded: [],
      lastHealthCheck: null,
      uptime: Date.now()
    };
    
    // Initialize degradation counters
    Object.values(Feature).forEach(feature => {
      this.stats.degradationsByFeature[feature] = 0;
    });
    
    // Health check timer
    this.healthCheckTimer = null;
    
    console.log('🛡️ Graceful degradation manager initialized');
  }
  
  /**
   * Start health monitoring
   */
  startHealthMonitoring() {
    if (this.healthCheckTimer) {
      console.warn('⚠️ Health monitoring already started');
      return;
    }
    
    console.log('🏥 Starting health monitoring');
    
    this.healthCheckTimer = setInterval(() => {
      this.performHealthCheck();
    }, this.healthCheckInterval);
  }
  
  /**
   * Stop health monitoring
   */
  stopHealthMonitoring() {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
      console.log('🏥 Health monitoring stopped');
    }
  }
  
  /**
   * Perform health check
   */
  performHealthCheck() {
    this.stats.lastHealthCheck = Date.now();
    
    // Count degraded features
    const degradedFeatures = Array.from(this.featureStatus.entries())
      .filter(([_, status]) => !status.enabled)
      .map(([feature, _]) => feature);
    
    this.stats.currentlyDegraded = degradedFeatures;
    
    // Update health status
    const previousStatus = this.healthStatus;
    
    if (degradedFeatures.length === 0) {
      this.healthStatus = HealthStatus.HEALTHY;
    } else if (degradedFeatures.length <= 2) {
      this.healthStatus = HealthStatus.DEGRADED;
    } else if (degradedFeatures.length <= 4) {
      this.healthStatus = HealthStatus.UNHEALTHY;
    } else {
      this.healthStatus = HealthStatus.DOWN;
    }
    
    // Log status change
    if (previousStatus !== this.healthStatus) {
      console.log(`🏥 Health status changed: ${previousStatus} → ${this.healthStatus}`);
    }
    
    // Attempt auto-recovery if enabled
    if (this.enableAutoRecovery && degradedFeatures.length > 0) {
      this.attemptAutoRecovery();
    }
  }
  
  /**
   * Record operation success
   */
  recordSuccess(feature) {
    const status = this.featureStatus.get(feature);
    if (!status) {
      console.warn(`⚠️ Unknown feature: ${feature}`);
      return;
    }
    
    status.successCount++;
    status.lastSuccess = Date.now();
    this.consecutiveSuccesses++;
    this.consecutiveFailures = 0;
    
    // Check if feature should be re-enabled
    if (!status.enabled && status.successCount >= this.recoveryThreshold) {
      this.enableFeature(feature);
    }
  }
  
  /**
   * Record operation failure
   */
  recordFailure(feature) {
    const status = this.featureStatus.get(feature);
    if (!status) {
      console.warn(`⚠️ Unknown feature: ${feature}`);
      return;
    }
    
    status.failureCount++;
    status.lastFailure = Date.now();
    this.consecutiveFailures++;
    this.consecutiveSuccesses = 0;
    
    // Check if feature should be degraded
    if (status.enabled && status.failureCount >= this.degradationThreshold) {
      this.degradeFeature(feature);
    }
  }
  
  /**
   * Degrade a feature
   */
  degradeFeature(feature) {
    const status = this.featureStatus.get(feature);
    if (!status || !status.enabled) {
      return;
    }
    
    status.enabled = false;
    status.failureCount = 0; // Reset counter
    this.stats.totalDegradations++;
    this.stats.degradationsByFeature[feature]++;
    
    console.warn(`⚠️ Feature degraded: ${feature}`);
    
    // Perform health check
    this.performHealthCheck();
  }
  
  /**
   * Enable a feature
   */
  enableFeature(feature) {
    const status = this.featureStatus.get(feature);
    if (!status || status.enabled) {
      return;
    }
    
    status.enabled = true;
    status.successCount = 0; // Reset counter
    this.stats.totalRecoveries++;
    
    console.log(`✅ Feature recovered: ${feature}`);
    
    // Perform health check
    this.performHealthCheck();
  }
  
  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(feature) {
    const status = this.featureStatus.get(feature);
    return status ? status.enabled : false;
  }
  
  /**
   * Get fallback behavior for feature
   */
  getFallback(feature) {
    const fallbacks = {
      [Feature.REAL_TIME_NOTIFICATIONS]: {
        message: 'Real-time notifications unavailable. Using polling instead.',
        action: 'USE_POLLING',
        interval: 60000 // 1 minute
      },
      [Feature.NOTIFICATION_HISTORY]: {
        message: 'Notification history unavailable. Showing cached data.',
        action: 'USE_CACHE',
        maxAge: 3600000 // 1 hour
      },
      [Feature.NOTIFICATION_BADGES]: {
        message: 'Notification badges unavailable.',
        action: 'HIDE_BADGES'
      },
      [Feature.TOAST_NOTIFICATIONS]: {
        message: 'Toast notifications unavailable.',
        action: 'USE_CONSOLE_LOG'
      },
      [Feature.SOUND_NOTIFICATIONS]: {
        message: 'Sound notifications unavailable.',
        action: 'SILENT_MODE'
      }
    };
    
    return fallbacks[feature] || {
      message: 'Feature unavailable.',
      action: 'DISABLE'
    };
  }
  
  /**
   * Attempt auto-recovery for degraded features
   */
  attemptAutoRecovery() {
    const degradedFeatures = Array.from(this.featureStatus.entries())
      .filter(([_, status]) => !status.enabled)
      .map(([feature, _]) => feature);
    
    if (degradedFeatures.length === 0) {
      return;
    }
    
    console.log(`🔄 Attempting auto-recovery for ${degradedFeatures.length} features`);
    
    // Reset success counters to allow recovery attempts
    degradedFeatures.forEach(feature => {
      const status = this.featureStatus.get(feature);
      if (status) {
        // Check if enough time has passed since last failure (5 minutes)
        const timeSinceFailure = Date.now() - (status.lastFailure || 0);
        if (timeSinceFailure > 300000) { // 5 minutes
          status.successCount = 0;
          console.log(`🔄 Ready to retry feature: ${feature}`);
        }
      }
    });
  }
  
  /**
   * Get health status
   */
  getHealthStatus() {
    return this.healthStatus;
  }
  
  /**
   * Get feature status
   */
  getFeatureStatus(feature) {
    return this.featureStatus.get(feature);
  }
  
  /**
   * Get all feature statuses
   */
  getAllFeatureStatuses() {
    return Array.from(this.featureStatus.entries()).map(([feature, status]) => ({
      feature,
      ...status
    }));
  }
  
  /**
   * Get statistics
   */
  getStats() {
    return {
      ...this.stats,
      healthStatus: this.healthStatus,
      consecutiveFailures: this.consecutiveFailures,
      consecutiveSuccesses: this.consecutiveSuccesses,
      uptime: Date.now() - this.stats.uptime
    };
  }
  
  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalDegradations: 0,
      totalRecoveries: 0,
      degradationsByFeature: {},
      currentlyDegraded: [],
      lastHealthCheck: null,
      uptime: Date.now()
    };
    
    // Re-initialize degradation counters
    Object.values(Feature).forEach(feature => {
      this.stats.degradationsByFeature[feature] = 0;
    });
    
    console.log('📊 Graceful degradation statistics reset');
  }
}

